import { useState, useEffect, useCallback } from "react";
import { Button, Card, Modal, Spinner } from "react-bootstrap";
import { AiOutlineClose } from "react-icons/ai";
import { useHistory } from "react-router-dom";
import { Plan, ePlanType } from "../../@types/Billing/Plan";
import { useCustomer } from "../../hooks/useCustomer";
import { eStripeStatus } from "../../@types/Billing/Subscription";
import { isEmpty } from "lodash";
import usePlan from "../../hooks/usePlan";
import useSubscription from "../../hooks/useSubscription";
import ErrorModal from "../../components/shared/ErrorModal";
import { availablePlans } from "../../components/billing/plans/ShowPlans/data";
import { BsToggle2Off, BsToggle2On } from "react-icons/bs";
import { CgDanger } from "react-icons/cg";
import ConfirmModal from "../../components/shared/ConfirmModal";
import { MdSpeed } from "react-icons/md";
import moment from "moment";
import GenericModal from "../../components/shared/GenericModal";
import { GiPartyPopper } from "react-icons/gi";
import { projectPrimaryColor } from "../../generalConfigs";

export default function UpdatePlan() {
  //States
  const [planType, setPlanType] = useState<ePlanType>(ePlanType.MONTHLY);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [isUserTrialing, setIsUserTrialing] = useState<boolean>(false);
  const [isPaymentFailed, setIsPaymentFailed] = useState<boolean>(false);
  const [isLoadingUpdate, setIsLoadingUpdate] = useState<boolean>(false);
  const [localRelationedPlans, setLocalRelationedPlans] = useState<Plan[]>([]);
  const [displayablePlans, setDisplayablePlans] = useState<Plan[]>([]);
  const [selectedNewPlan, setSelectedNewPlan] = useState<Plan>({} as Plan);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [isUpdateFailed, setIsUpdateFailed] = useState<boolean>(false);
  const [isTooEarly, setIsTooEarly] = useState<boolean>(false);

  //Hooks
  const { goBack, push } = useHistory();
  const { getPlans } = usePlan();
  const { isUserPremiumUncached, updateSubscription } = useSubscription();
  const { getEnabledPlan } = useCustomer();

  async function determineCanContinue() {
    const userPlan = await getEnabledPlan();

    if (!userPlan) {
      setIsError(true);
      return;
    }

    if (userPlan.stripe_status === eStripeStatus.TRIALING) {
      // Determines if the user is on trial
      setIsUserTrialing(true);
    } else if (userPlan.last_user_update) {
      // Determines if the user is update on last 90 days
      const lastUpdateDiffInDays = moment().diff(
        moment(userPlan.last_user_update, "YYYY-MM-DD HH:mm:ss"),
        "days"
      );

      if (lastUpdateDiffInDays < 90) {
        setIsTooEarly(true);
      }
    }
  }

  async function loadPlans() {
    setIsLoading(true);
    setIsError(false);

    const [storedPlans, isAlreadyPremium] = await Promise.all([
      getPlans(),
      isUserPremiumUncached(),
      determineCanContinue(),
    ]);

    if (!isAlreadyPremium) {
      push("/billing");
    }

    // For every plan from backend, we iterate over it and find local plan with the same name,
    // after it we add price_id from backend on local plan's array
    if (!isEmpty(storedPlans)) {
      const relationedPlans: Plan[] = [];

      storedPlans.forEach((storedPlan) => {
        const localPlan = availablePlans.find(
          (availablePlan) => availablePlan.identifier === storedPlan.identifier
        );

        if (localPlan) {
          localPlan.priceId = storedPlan.price_id;
          relationedPlans.push(localPlan);
        }
      });

      setLocalRelationedPlans(relationedPlans);

      const newDisplayablePlansState = relationedPlans.filter(
        (plan) => plan.type === planType
      );
      setDisplayablePlans(newDisplayablePlansState);
    } else {
      setIsError(true);
    }

    setIsLoading(false);
  }

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newDisplayablePlans = localRelationedPlans.filter(
      (plan) => plan.type === planType
    );
    setDisplayablePlans(newDisplayablePlans);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planType]);

  const handlePlanTypeChange = useCallback(() => {
    if (planType === ePlanType.MONTHLY) {
      setPlanType(ePlanType.ANNUALY);
    } else {
      setPlanType(ePlanType.MONTHLY);
    }
  }, [planType]);

  function handlePlanSelect(plan: Plan) {
    setSelectedNewPlan(plan);
    setShowConfirmModal(true);
  }

  async function handleSubmit() {
    setIsLoadingUpdate(true);

    const response = await updateSubscription({
      priceId: String(selectedNewPlan.priceId),
      planIdentifier: selectedNewPlan.identifier,
    });

    if (response.status === 200) {
      setIsFinished(true);
    } else if (response.status === 402) {
      setIsPaymentFailed(true);
    } else if (response.status === 403 && response.msg === "Too early") {
      setIsTooEarly(true);
    } else {
      setIsUpdateFailed(true);
    }

    setIsLoadingUpdate(false);
  }

  // Views
  if (isUserTrialing) {
    return (
      <ErrorModal
        title="Ooops!"
        description="Você não pode atualizar seu plano enquanto está no período de teste do seu plano, volte aqui novamente quando seu período de teste expirar."
        action={{
          title: "Voltar para Dashboard",
          onClickCallback: () => push("/dashboard"),
        }}
      />
    );
  } else if (isTooEarly) {
    return (
      <ErrorModal
        icon={<MdSpeed size={70} />}
        title="Ooops!"
        description="Você já trocou seu plano nos últimos 90 dias, você só pode atualizar seu plano a cada 90 dias, por favor, tente novamente após o período mínimo determinado."
        action={{
          title: "Voltar para Dashboard",
          onClickCallback: () => push("/dashboard"),
        }}
      />
    );
  } else if (isFinished) {
    return (
      <GenericModal
        title="Parabéns!!"
        description="Seu plano foi atualizado com sucesso, você já pode aproveitar todas as funcionalidades do seu plano clicando no botão abaixo."
        icon={<GiPartyPopper size={70} color={projectPrimaryColor} />}
        actions={[
          {
            isLoading: false,
            onClickHandler: () => push("/dashboard"),
            title: "Ir para Dashboard",
            variant: "primary",
          },
        ]}
      />
    );
  } else if (isPaymentFailed) {
    return (
      <ErrorModal
        isLoading={isLoadingUpdate}
        title="Falha no pagamento"
        description="Ao trocar um plano mensal para anual (ou vice-versa), é feita uma cobrança imediata na sua forma de pagamento padrão, verifique se sua forma de pagamento padrão está correta e tente novamente."
        action={{
          title: "Tentar novamente",
          onClickCallback: handleSubmit,
        }}
      />
    );
  } else if (isUpdateFailed) {
    return (
      <ErrorModal
        isLoading={isLoadingUpdate}
        title="Erro desconhecido"
        description="Infelizmente, ocorreu um erro desconhecido ao processar sua nova assinatura, por favor, tente novamente."
        action={{
          title: "Tentar novamente",
          onClickCallback: handleSubmit,
        }}
      />
    );
  } else if (showConfirmModal) {
    return (
      <ConfirmModal
        isLoading={isLoadingUpdate}
        title="Você tem certeza?"
        description="Sua próxima fatura vai ter o valor do seu novo plano escolhido e você começara a desfrutar do plano imediatamente, você só poderá trocar de plano novamente em 90 dias. Caso esteja saindo de um plano mensal para um plano anual (ou vice-versa) você será cobrado imediatamente com o valor do novo plano escolhido."
        confirmTitle="Confirmar"
        onClickConfirm={handleSubmit}
        onClickBack={() => setShowConfirmModal(false)}
      />
    );
  } else {
    return (
      <Modal
        size="lg"
        fullscreen={true}
        show={true}
        dialogClassName="plans-modal-dialog"
        contentClassName="empty-background "
      >
        <div className="container mw-95">
          <div className="row">
            <div
              className="col-lg-12 shadow-lg d-flex align-items-center rounded-3 rounded-start-0 "
              style={{
                backgroundColor: "var(--bg-1)",
                borderRadius: 10,
              }}
            >
              <div className="container my-auto py-4 pd-l-10 pd-r-10">
                <div className="row">
                  <div className="col-12 mx-auto">
                    <div className="float-right cursor-pointer">
                      <AiOutlineClose size={22} onClick={() => goBack()} />
                    </div>

                    <div className="d-flex align-itens-center justify-content-center">
                      <h3 className="text-black text-center mb-3">
                        Selecione seu novo plano
                      </h3>
                    </div>

                    <div className="row">
                      {!isLoading && !isError && (
                        <div className="col-12">
                          <div className="d-flex align-itens-center justify-content-center">
                            <span
                              className={`mg-r-10 not-selectable ${
                                planType === ePlanType.MONTHLY && "tx-primary"
                              }`}
                              style={{ cursor: "default" }}
                            >
                              Mensal{" "}
                            </span>

                            {getSwitch(planType, handlePlanTypeChange)}
                            <span
                              className={`mg-l-10 not-selectable ${
                                planType === ePlanType.ANNUALY && "tx-primary"
                              }`}
                              style={{ cursor: "default" }}
                            >
                              {" "}
                              Anual
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="d-flex align-itens-center justify-content-center">
                      {isLoading && (
                        <div className="mg-t-20 mg-b-20">
                          <Spinner animation="border"></Spinner>
                        </div>
                      )}
                    </div>

                    {isError && getErrorCard(loadPlans)}

                    <div className="row">
                      {!isError &&
                        !isLoading &&
                        displayablePlans.map((plan, i) => {
                          return (
                            <div
                              key={i}
                              className="col-12 col-md-6 col-lg-4 card-group"
                            >
                              {PlanCard(plan, handlePlanSelect)}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  }
}

function getSwitch(planType: ePlanType, handleOnClick: CallableFunction) {
  if (planType === ePlanType.MONTHLY) {
    return (
      <BsToggle2Off
        cursor={"pointer"}
        style={{ marginTop: -6, marginBottom: 10 }}
        size={30}
        onClick={() => handleOnClick()}
      />
    );
  } else {
    return (
      <BsToggle2On
        cursor={"pointer"}
        style={{ marginTop: -6, marginBottom: 10 }}
        size={30}
        onClick={() => handleOnClick()}
      />
    );
  }
}

function getErrorCard(handleOnClick: CallableFunction) {
  return (
    <Card className="pd-10 rounded-button">
      <div className="row">
        <div className="col-12 centered-flex">
          <CgDanger size={35} />
        </div>

        <div className="col-12 centered-flex">
          <h2>Ops</h2>
        </div>

        <div className="col-12 centered-flex">
          <p className="tx-center">
            Ocorreu um erro desconhecido ao buscar planos disponíveis, por
            favor, tente novamente.
          </p>
        </div>

        <div className="col-12 centered-flex">
          <Button
            className="rounded-button"
            variant="primary"
            onClick={() => handleOnClick()}
          >
            Tentar novamente
          </Button>
        </div>
      </div>
    </Card>
  );
}

function PlanCard(plan: Plan, clickButtonAction: CallableFunction) {
  return (
    <Card
      className="shadow"
      style={{ borderRadius: 15, padding: 15, marginTop: 10 }}
    >
      <div className="centered-flex">{plan.icon.element}</div>

      <p className="tx-18 tx-roboto tx-medium  text-center">{plan.title}</p>

      <p
        style={{ marginTop: -15 }}
        className="tx-30 tx-roboto tx-medium  text-center "
      >
        <span className="tx-16 tx-light ">R$ </span>
        {plan.price}
        <span className="tx-16 tx-light">
          {plan.type === ePlanType.MONTHLY ? "/Mês" : "/Ano"}
        </span>
      </p>

      <div className="centered-flex">
        <ul className="list-custom mb-20px">
          {plan.features.map((feature, i) => {
            return <li key={i}>{feature}</li>;
          })}
        </ul>
      </div>

      <div className="centered-flex">
        <Button
          style={{ borderRadius: 15 }}
          variant="primary"
          onClick={() => clickButtonAction(plan)}
        >
          Atualizar
        </Button>
      </div>
    </Card>
  );
}
